// Flare Barrage (owner 2026-11-02: 'much smaller energy blasts shot with both hands extended forward, fired rapidly at
// machine-gun speed, each one bursting in a mini fireball on impact'). Render-only: reads sim.barrageShots /
// sim.barrageVolley and the 'barrage' / 'barragePop' events; decides nothing about gameplay.
//  - pellets: tiny warm-yellow streak cards with a white-hot head and a short tail (~0.38 m), one pooled InstancedMesh plus
//    a faint additive halo; they leave the real hand (bone / forearm tip) and ease onto the sim path in ~0.1 s
//  - a small muzzle flash at the hand that fired; a mini fireball pop on every burst (small cel fire lobe, white core
//    flash sized by the burst radius, a few sparks, a ground flash and now and then a tiny scorch)
//  - the hands-forward pose: the approved Nova Lance release key (same reuse as Rift Mortar), held for the volley
// The skill keeps its own yellow identity at every Surge level. No per-shot meshes, no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, float, mix, smoothstep, uv, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import { FLASH, softDotTexture } from './fx';
import type { GroundGlow } from './groundGlow';
import type { GltfHero } from './heroGltf';
import { ACTIVE_LANCE, LANCE_POSES } from './heroPoses';
import type { HeroRig } from './models';

const POOL = 160;                       // pellets drawn at once (sim pool size)
const LEN = 0.55, WID = 0.24;           // streak card length / width (m): small, but must read at the gameplay camera
const EASE_T = 0.1;                     // a fresh pellet eases from the hand onto its sim path over this (s)
const HAND_Y = 1.25;                    // fallback muzzle height above the ground when no hand is known
/** identity colours: warm yellow body, white-hot head, orange-yellow burst */
const BODY = 0xffd84a, HOT = 0xfff6d8, POP_TINT = 0xffa630, POP_GLOW = 0xffb020;

type BarrageEv = Extract<SimEvent, { t: 'barrage' }>;
type PopEv = Extract<SimEvent, { t: 'barragePop' }>;
interface Rec { ox: number; oz: number; y: number; age: number }

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion();
const _d = new THREE.Vector3(), _dl = new THREE.Vector3(), _side = new THREE.Vector3(), _n = new THREE.Vector3(), _v = new THREE.Vector3();
const _body = new THREE.Color(BODY), _rim = new THREE.Color(0xff7a10);

export class BarrageFx {
  /** 0..1 hands-forward pose weight (view.ts applies it to the hero) and the world yaw of the stream */
  pose = 0; aimYaw = 0;
  /** QA counters */
  fired = 0; pops = 0;
  private hold = 0;
  private streak: THREE.InstancedMesh; private halo: THREE.InstancedMesh;
  private recs = new Map<number, Rec>();
  /** both hands in world space this frame (index 0 = the hand on the +1 side of the aim, 1 = the -1 side) and whether known */
  private hands = [new THREE.Vector3(), new THREE.Vector3()]; private handsOk = false;
  private popsFrame = 0; private scorchN = 0;

  constructor(scene: THREE.Scene, private fx: Fx, private glowOut: GroundGlow, private groundAt: (x: number, z: number) => number, private burn: (x: number, z: number, r: number) => void) {
    // streak card: x from -1 (tail) to 0 (head), y across; uv.x 0 tail → 1 head
    const g = new THREE.PlaneGeometry(1, 1); g.translate(-0.5, 0, 0);
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const u = uv().x, across = abs(uv().y.sub(0.5)).mul(2);
    const cap = u.sub(0.72).div(0.28).max(0), head = float(1).sub(cap.mul(cap)).max(0).sqrt(); // round head
    const w = smoothstep(0.0, 0.72, u).mul(0.8).add(0.2).min(head);                                // tapering tail
    const shape = smoothstep(w, w.mul(0.85), across);
    const inner = smoothstep(w.mul(0.8), w.mul(0.6), across);                                     // crisp orange rim outside this
    const core = smoothstep(w.mul(0.5), w.mul(0.25), across).mul(smoothstep(0.3, 0.85, u));
    // orange rim (reads against the bright grass) → saturated yellow body → white-hot head
    m.colorNode = mix(mix(vec3(_rim.r * 1.1, _rim.g * 1.1, _rim.b), vec3(_body.r * 1.2, _body.g * 1.1, _body.b * 0.6), inner), vec3(1.3, 1.25, 1.1), core);
    m.opacityNode = shape.mul(smoothstep(0.0, 0.5, u).mul(0.75).add(0.25));
    this.streak = new THREE.InstancedMesh(g, m, POOL); this.streak.count = 0; this.streak.frustumCulled = false; this.streak.renderOrder = 5;
    const hm = new THREE.MeshBasicNodeMaterial({ map: softDotTexture(), color: new THREE.Color(0xffc23a).multiplyScalar(0.8), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    this.halo = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), hm, POOL); this.halo.count = 0; this.halo.frustumCulled = false; this.halo.renderOrder = 4;
    scene.add(this.streak, this.halo);
  }

  /** 'barrage' event: a pellet leaves a hand. Remember where the drawn hand is so the pellet starts there; muzzle flash. */
  shot(ev: BarrageEv): void {
    this.fired++;
    const h = this.handsOk ? this.hands[ev.hand > 0 ? 0 : 1] : null;
    const hx = h ? h.x + ev.dx * 0.12 : ev.x, hz = h ? h.z + ev.dz * 0.12 : ev.z, hy = h ? h.y : this.groundAt(ev.x, ev.z) + HAND_Y;
    this.recs.set(ev.id, { ox: hx - ev.x, oz: hz - ev.z, y: hy, age: 0 });
    const fx = this.fx, big = ev.evolved;
    fx.stars.add(hx, hy, hz, big ? 0.7 : 0.55, 0.05, HOT, 2.4);
    fx.particles.emitDir(hx, hy, hz, ev.dx * (7 + Math.random() * 4) + (Math.random() - 0.5) * 2, Math.random() * 1.5, ev.dz * (7 + Math.random() * 4) + (Math.random() - 0.5) * 2, BODY, 0.09, 0.08, 2.4);
    if (ev.k % 6 === 0 && FLASH.dim >= 1) fx.lights.flash(hx, hy, hz, 0xffc040, 5, 0.07, 4); // a light now and then, not per shot
  }

  /** 'barragePop' event: a mini fireball (kept cheap: a busy frame skips the cel fire lobe and smoke). */
  pop(ev: PopEv): void {
    this.pops++;
    const rec = this.recs.get(ev.id); this.recs.delete(ev.id);
    const gy = this.groundAt(ev.x, ev.z), y = rec ? Math.max(gy + 0.5, rec.y) : gy + HAND_Y;
    const fx = this.fx, r = ev.r, heavy = ++this.popsFrame <= 5;
    fx.stars.add(ev.x, y, ev.z, r * 1.1, 0.07, HOT, 2.6);               // white-hot core
    fx.stars.add(ev.x, y, ev.z, r * 2.0, 0.13, POP_TINT, 1.5);          // orange-yellow puff
    if (heavy) fx.blast?.pop(ev.x, y, ev.z, POP_TINT);                  // small cel fire lobe + sparks + a wisp of smoke
    for (let k = 0; k < (heavy ? 4 : 2); k++) {
      const a = Math.random() * 6.283, sp = 3 + Math.random() * 4;
      fx.particles.emitDir(ev.x, y, ev.z, Math.cos(a) * sp, 1 + Math.random() * 3, Math.sin(a) * sp, k % 2 ? BODY : POP_TINT, 0.12, 0.18 + Math.random() * 0.1, 2.4);
    }
    this.glowOut.flash(ev.x, ev.z, r * 1.4, POP_GLOW, 0.45, 0.14);
    if ((this.scorchN = (this.scorchN + 1) % 4) === 0) this.burn(ev.x, ev.z, r * 0.55); // a tiny scorch every 4th burst
  }

  /** Per frame: hand positions, pose weight, pellets. gltf = the authored hero when it is the drawn model (else the rig). */
  update(dt: number, sim: Sim, cam: THREE.Camera, rig: HeroRig, gltf: GltfHero | null): void {
    this.popsFrame = 0;
    const v = sim.barrageVolley, p = sim.player;
    if (v) { this.hold = 0.18; this.aimYaw = Math.atan2(v.dx, v.dz); }
    this.hold = Math.max(0, this.hold - dt);
    const want = v || this.hold > 0 ? 1 : 0;
    this.pose += (want - this.pose) * Math.min(1, dt * (want ? 18 : 7));
    // both hands, sorted to the aim sides the sim uses (+1 = (-dz, dx) side)
    this.handsOk = false;
    if (this.pose > 0.05) {
      const [a, b] = this.hands;
      if (gltf) { gltf.handWorld('L', a); gltf.handWorld('R', b); }
      else { rig.root.updateMatrixWorld(true); rig.foreL.localToWorld(a.set(0, -0.36, 0.06)); rig.foreR.localToWorld(b.set(0, -0.36, 0.06)); }
      const ax = Math.sin(this.aimYaw), az = Math.cos(this.aimYaw), side = (q: THREE.Vector3) => (q.x - p.x) * -az + (q.z - p.z) * ax;
      if (side(a) < side(b)) { _v.copy(a); a.copy(b); b.copy(_v); }
      this.handsOk = true;
    }
    // pellets
    const camPos = cam.position;
    let n = 0;
    for (const b of sim.barrageShots) {
      if (!b.alive || n >= POOL) continue;
      let r = this.recs.get(b.id);
      if (!r) { r = { ox: 0, oz: 0, y: this.groundAt(b.x, b.z) + HAND_Y, age: EASE_T }; this.recs.set(b.id, r); }
      r.age += dt;
      const e = Math.max(0, 1 - r.age / EASE_T), k = e * e;
      const x = b.x + r.ox * k, z = b.z + r.oz * k, y = r.y;
      const sp = Math.hypot(b.vx, b.vz) || 1;
      _d.set(b.vx / sp, 0, b.vz / sp);
      _side.set(camPos.x - x, camPos.y - y, camPos.z - z).cross(_d).normalize(); // across the streak, facing the camera
      _n.copy(_d).cross(_side);
      const L = LEN * (b.evo ? 1.15 : 1), W = WID * (b.evo ? 1.15 : 1);
      _m.makeBasis(_dl.copy(_d).multiplyScalar(L), _side.multiplyScalar(W), _n); _m.setPosition(x, y, z);
      this.streak.setMatrixAt(n, _m);
      _p.set(x - _d.x * L * 0.25, y, z - _d.z * L * 0.25); _q.copy(cam.quaternion); _s.setScalar(b.evo ? 0.95 : 0.8);
      this.halo.setMatrixAt(n, _m.compose(_p, _q, _s));
      n++;
    }
    this.streak.count = n; this.halo.count = n;
    this.streak.instanceMatrix.needsUpdate = true; this.halo.instanceMatrix.needsUpdate = true;
    // forget records of pellets that are gone without a pop we saw (pool reuse, run reset)
    if (this.recs.size > POOL * 2) { const live = new Set(sim.barrageShots.filter((q) => q.alive).map((q) => q.id)); for (const id of this.recs.keys()) if (!live.has(id)) this.recs.delete(id); }
  }

  /** Ground glow: a small warm pool under each pellet in flight (groundGlow.ts). */
  glowPools(g: GroundGlow, sim: Sim): void {
    let n = 0;
    for (const b of sim.barrageShots) {
      if (!b.alive || n++ >= 48) continue;
      const r = this.recs.get(b.id), y = r ? r.y : this.groundAt(b.x, b.z) + HAND_Y;
      g.blob(b.x, y, b.z, b.evo ? 0.85 : 0.7, 0xffc020, 0.35);
    }
  }

  /** Hands-forward pose for the procedural rig: the approved Nova Lance release key (two palms forward), arms only, so the
   *  legs keep their walk cycle (same reuse as Rift Mortar's mortarBall.ts poseRig). No new animation. */
  poseRig(rig: HeroRig): void {
    const w = this.pose; if (w <= 0.01) return;
    const k = LANCE_POSES[ACTIVE_LANCE].thrust, a = rig.armR.rotation, b = rig.armL.rotation;
    a.set(a.x + (k.armR[0] - a.x) * w, a.y + (k.armR[1] - a.y) * w, a.z + (k.armR[2] - a.z) * w);
    b.set(b.x + (k.armL[0] - b.x) * w, b.y + (k.armL[1] - b.y) * w, b.z + (k.armL[2] - b.z) * w);
    rig.foreR.rotation.x += (k.foreR - rig.foreR.rotation.x) * w; rig.foreL.rotation.x += (k.foreL - rig.foreL.rotation.x) * w;
  }

  clear(): void { this.recs.clear(); this.streak.count = 0; this.halo.count = 0; this.pose = 0; this.hold = 0; }
}
